import { Button, Box, CircularProgress, SxProps, Typography, IconButton, MenuItem, Select, SelectChangeEvent, Collapse, Chip } from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import React, { useMemo, useState, useEffect } from 'react';
import { WrappedEpisodeOfCare } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';
import CustomDrawer, { CustomDrawerProps } from 'src/components/CustomDrawer';
import { formatDateTimeToDefault } from 'src/utils/dates';
import { spreadSxProp } from 'src/utils/cssStyles';
import { capitalize } from 'src/utils/string';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { useSnackbar } from 'notistack';
import { useEpisodeOfCares } from 'src/careflow/ph-core/fhir-client/fhir-react';

const { valueTextFontSize, blackTextColor }: { [k: string]: SxProps } = {
  valueTextFontSize: {
    fontSize: '0.8rem',
  },
  blackTextColor: {
    color: '#212b36',
  },
};

export const Container = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => (
  <Box
    sx={[
      {
        px: 2.8,
      },
      ...spreadSxProp(sx),
    ]}
  >
    {children}
  </Box>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

export const Section = ({ title, children }: SectionProps) => (
  <Box sx={{ my: 3 }}>
    <Typography sx={{ fontWeight: 'bold', mb: 3 }}>{title}</Typography>
    {children}
  </Box>
);

interface LabelValueProps {
  label: string;
  value: string | React.ReactNode;
  labelSx?: SxProps;
}

export const LabelValue = ({ label, value, labelSx }: LabelValueProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', my: 2 }}>
    <Typography sx={[valueTextFontSize, { flex: 1, color: '#637381' }, ...spreadSxProp(labelSx)]}>
      {label}
    </Typography>
    <Typography sx={[valueTextFontSize, blackTextColor, { flex: 2 }]}>{value}</Typography>
  </Box>
);

interface EpisodeOfCareDetailDrawerProps extends Omit<CustomDrawerProps, 'title' | 'anchor'> {
  episodeOfCare: WrappedEpisodeOfCare | null;
  onEdit: (episodeOfCare: WrappedEpisodeOfCare) => void;
  onSuccesfulEdit?: () => void;
  isAllowedToEdit: boolean;
  title?: string;
}

const EpisodeOfCareDetailDrawer = ({
  episodeOfCare,
  onEdit,
  onSuccesfulEdit,
  isAllowedToEdit,
  title,
  ...props
}: EpisodeOfCareDetailDrawerProps) => {
  type EpisodeOfCareStatusType = 'planned' | 'waitlist' | 'active' | 'onhold' | 'finished' | 'cancelled' | 'entered-in-error';
  
  const [selectedStatus, setSelectedStatus] = useState<EpisodeOfCareStatusType>(
    (episodeOfCare?.status as EpisodeOfCareStatusType) ?? 'active'
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isStatusHistoryExpanded, setIsStatusHistoryExpanded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  
  const [episodeOfCareStatusVS] = useValueSet('episode-of-care-status', {
    map: ValueSetWrapper,
  });

  const [, { update }] = useEpisodeOfCares({ autofetch: false, client: 'indexed' });

  useEffect(() => {
    setSelectedStatus((episodeOfCare?.status as EpisodeOfCareStatusType) ?? 'active');
    setIsEditMode(false);
  }, [episodeOfCare]);

  useEffect(() => {
    if (!props?.open) {
      setIsEditMode(false);
      setIsStatusHistoryExpanded(false);
    }
  }, [props?.open]);
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setSelectedStatus(event.target.value as EpisodeOfCareStatusType);
  };

  const toggleEditMode = () => {
    setIsEditMode((isEditMode) => !isEditMode);
  };

  const handleStatusUpdate = async () => {
    if (!episodeOfCare?.id) return;
    
    try {
      const updatedResource = await update({
        id: episodeOfCare.id,
        resourceType: 'EpisodeOfCare',
        status: selectedStatus,
        patient: episodeOfCare.patient,
        type: episodeOfCare.type,
        period: episodeOfCare.period,
        careManager: episodeOfCare.careManager,
        statusHistory: episodeOfCare.statusHistory,
      });
      
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
      setIsEditMode(false);
      if (onSuccesfulEdit) {
        onSuccesfulEdit();
      }
    } catch (error) {
      enqueueSnackbar('Unable to update Status. Please Try Again', { variant: 'error' });
    }
  };

  const episodeDetails = useMemo(() => {
    if (!episodeOfCare) return [];

    const typeDisplay = episodeOfCare?.type?.[0]?.coding?.[0]?.display || 
                       episodeOfCare?.type?.[0]?.text || 
                       '';

    return [
      { label: 'Type', value: typeDisplay },
      { 
        label: 'Last Modified Date', 
        value: formatDateTimeToDefault(episodeOfCare?.meta?.lastUpdated) || '' 
      },
      { 
        label: 'Start Date', 
        value: formatDateTimeToDefault(episodeOfCare?.period?.start) || '' 
      },
      { 
        label: 'End Date', 
        value: formatDateTimeToDefault(episodeOfCare?.period?.end) || '' 
      },
      { label: 'Status', value: '' },
      { 
        label: 'Care Manager', 
        value: episodeOfCare?.careManager?.display || '' 
      },
    ];
  }, [episodeOfCare]);

  const statusHistory = useMemo(() => {
    if (!episodeOfCare?.statusHistory || episodeOfCare.statusHistory.length === 0) {
      return [];
    }

    const extensionUrl = 'https://terminology.patientholistics.app/fhir/StructureDefinition/episodeofcare-status-history-reason';
    
    return episodeOfCare.statusHistory.map((entry: any) => {
      const reasonExtension = entry?.extension?.find((ext: any) => ext.url === extensionUrl);
      const reason = reasonExtension?.valueCodeableConcept?.coding?.[0]?.display || 
                     reasonExtension?.valueCodeableConcept?.text || 
                     '';

      return {
        status: capitalize(entry?.status || ''),
        start: formatDateTimeToDefault(entry?.period?.start) || '',
        end: formatDateTimeToDefault(entry?.period?.end) || '',
        reason: reason,
      };
    });
  }, [episodeOfCare]);

  return (
    <CustomDrawer
      {...props}
      title={title || 'Episode Of Care Details'}
      anchor="right"
      containerSx={{ width: '25vw' }}
      contentContainerSx={{ padding: 0 }}
    >
      {episodeOfCare ? (
        <>
          <Container sx={{ backgroundColor: '#f4f6f8' }}>
            <Section title="Detail">
              {episodeDetails.map((detail) => (
                <React.Fragment key={detail.label}>
                  {detail.label !== 'Status' ? (
                    <LabelValue {...detail} />
                  ) : (
                    <LabelValue
                      label={detail.label}
                      labelSx={{ alignSelf: 'center' }}
                      value={
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          justifyContent={'space-between'}
                          alignItems={'center'}
                        >
                          {!isEditMode ? (
                            <Chip
                              label={episodeOfCareStatusVS?.asListAll?.()?.find?.((opt) => opt.code === selectedStatus)?.display || capitalize(selectedStatus)}
                              size="small"
                              sx={{
                                backgroundColor: '#f4f6f8',
                                color: '#637381',
                                fontWeight: 'normal',
                                height: '24px',
                                fontSize: '0.75rem',
                                flex: 1,
                                maxWidth: '60%',
                              }}
                            />
                          ) : (
                            <Select
                              value={selectedStatus}
                              onChange={handleStatusChange}
                              sx={{ height: 30, flex: 1 }}
                            >
                              {episodeOfCareStatusVS?.asListAll?.()?.map?.((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                  {option.display}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                          <IconButton
                            disabled={!isAllowedToEdit}
                            onClick={() => {
                              if (isEditMode) {
                                handleStatusUpdate();
                              }
                              toggleEditMode();
                            }}
                          >
                            {isEditMode ? (
                              <SaveIcon htmlColor="#00ab55" />
                            ) : (
                              <EditIcon htmlColor="#919eab" />
                            )}
                          </IconButton>
                        </Box>
                      }
                    />
                  )}
                </React.Fragment>
              ))}
            </Section>
          </Container>

          {statusHistory.length > 0 && (
            <Container sx={{ mt: 3 }}>
              <Box sx={{ my: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    mb: isStatusHistoryExpanded ? 3 : 0,
                  }}
                  onClick={() => setIsStatusHistoryExpanded(!isStatusHistoryExpanded)}
                >
                  <Typography sx={{ fontWeight: 'bold', flex: 1 }}>Status History</Typography>
                  <IconButton size="small">
                    <ExpandMoreIcon
                      sx={{
                        transform: isStatusHistoryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Box>
                <Collapse in={isStatusHistoryExpanded}>
                  {statusHistory.map((entry, index) => (
                    <Box key={index} sx={{ my: 2, p: 2, backgroundColor: '#f9fafb', borderRadius: 1 }}>
                      <Typography sx={[valueTextFontSize, blackTextColor, { fontWeight: 'bold', mb: 1 }]}>
                        {entry.status}
                      </Typography>
                      <Typography sx={[valueTextFontSize, { color: '#637381', mb: 0.5 }]}>
                        {entry.start && entry.end 
                          ? `${entry.start} to ${entry.end}`
                          : entry.start || entry.end || ''}
                      </Typography>
                      {entry.reason && (
                        <Typography sx={[valueTextFontSize, blackTextColor, { mt: 1 }]}>
                          Reason: {entry.reason}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Collapse>
              </Box>
            </Container>
          )}

          <Container sx={{ mt: 3 }}>
            <Button
              disabled={!isAllowedToEdit}
              sx={{ height: 48 }}
              fullWidth
              onClick={() => {
                onEdit(episodeOfCare);
              }}
              variant="contained"
            >
              Edit
            </Button>
          </Container>
        </>
      ) : (
        <Box flex={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <CircularProgress />
        </Box>
      )}
    </CustomDrawer>
  );
};

export default EpisodeOfCareDetailDrawer;

